/**
 * The typed API client (openapi-fetch), generated from the committed
 * contract docs/api/openapi.json (`npm run api:types`).
 *
 * The session is an HttpOnly cookie the browser sends by itself. Every
 * state-changing request also sends the session's CSRF token, which
 * sign-in and "who am I" return (docs/adr/0008-built-in-accounts-and-sessions.md).
 */
import createClient, { type Middleware } from "openapi-fetch";

import type { components, paths } from "./schema";

export type Schemas = components["schemas"];
export type SignedInUser = Schemas["SignedInUser"];
export type ErrorCode = Schemas["ErrorCode"];

let csrfToken: string | null = null;

/** Remembers the CSRF token of the current session. */
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/** The current session's CSRF token, for requests made without the client. */
export function getCsrfToken(): string | null {
  return csrfToken;
}

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

const csrf: Middleware = {
  onRequest({ request }) {
    if (csrfToken && !SAFE_METHODS.has(request.method)) {
      request.headers.set("X-CSRF-Token", csrfToken);
    }
    return request;
  },
};

export const api = createClient<paths>({
  // The page's own origin: the API is always on the same one.
  baseUrl: globalThis.location.origin,
  credentials: "same-origin",
  // Looked up on each call rather than captured once, so tests can stub it.
  fetch: (request) => globalThis.fetch(request),
});
api.use(csrf);

/** An API error with its stable code, which the UI translates. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(status: number, code: string | null, detail: string) {
    super(detail);
    this.status = status;
    this.code = code;
  }
}

interface ErrorBody {
  code?: string;
  detail?: unknown;
}

/** The data of a response, or an `ApiError` thrown for anything but 2xx. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.response.ok && result.data !== undefined) {
    return result.data;
  }
  if (result.response.ok) {
    return undefined as T; // 204 No Content
  }
  const body = (result.error ?? {}) as ErrorBody;
  const detail = typeof body.detail === "string" ? body.detail : result.response.statusText;
  throw new ApiError(result.response.status, body.code ?? null, detail);
}
